import dayjs, { type Dayjs } from 'dayjs';

import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';
import STATUS_CODES from '@/constants/statuscode.constants';

import {
  buildGoogleStoragePayload,
  buildMicrosoftPayload,
  buildS3DataProviderPayload
} from './DataProvider/DataProvider.service';
import SOURCE_TYPE, {
  ROOT_LEVEL_ATTRIBUTES,
  UI_META_DATA_CONFIG
} from './SourceCreate.constants';
import {
  type BasicSourceLogTypeDTO,
  type ConnectionInputSchema,
  type ConnectionInputs,
  MICROSOFT_CHILD_SOURCE_TYPES,
  SOFT_EXPIRY_SOURCE_TYPES,
  type SourceCreateDynamicFormModel,
  type SourceCreateRequest,
  SourceDialogMode
} from './SourceCreate.types';

// ============================================================================
// Constants
// ============================================================================

/**
 * Source types that support certificate authentication.
 */
const CERTIFICATE_SUPPORTED_SOURCE_TYPES = [
  SOURCE_TYPE.O365,
  SOURCE_TYPE.MICROSOFT_SENTINEL,
  SOURCE_TYPE.MICROSOFT_DEFENDER,
  SOURCE_TYPE.MICROSOFT_ENTRA_ID,
  SOURCE_TYPE.MICROSOFT
] as const;

/**
 * Source types that support file upload (JSON credentials).
 */
const FILE_UPLOAD_SOURCE_TYPES = [
  SOURCE_TYPE.GOOGLE_WORKSPACE,
  SOURCE_TYPE.OKTA,
  SOURCE_TYPE.GOOGLE_PUB_SUB,
  SOURCE_TYPE.GOOGLE_STORAGE,
  SOURCE_TYPE.SNOWFLAKE
] as const;

// ============================================================================
// Date Formatting Utilities
// ============================================================================

/**
 * Converts a local date to UTC for backend storage.
 * All dates are stored in UTC to ensure consistency across timezones.
 */
const convertDateToUTC = (date: string | Dayjs): string => {
  return dayjs(date).utc().format(YEAR_MONTH_DAY_TIME_FORMAT);
};

/**
 * Formats a date value for display in the form.
 * Converts UTC dates from the backend to local timezone for user display.
 */
export const formatIfValidDate = (
  value: string | number | Dayjs,
  name: string,
  dateFormat: string,
  dateTimeFormat: string,
  defaultValues: SourceCreateDynamicFormModel
): SourceCreateDynamicFormModel => {
  const parsed = dayjs(value);
  const isValidDate = dayjs(
    parsed.format(dateFormat),
    dateFormat,
    true
  ).isValid();

  if (isValidDate) {
    // The ingest_from date is converted from UTC to the local timezone.
    const formattedDate = dayjs.utc(value).local().format(dateTimeFormat);
    return {
      ...defaultValues,
      [name]: formattedDate || null
    };
  }

  return defaultValues;
};

// ============================================================================
// File Reading Utilities
// ============================================================================

/**
 * Reads a file and converts it to Base64 string.
 * Used for uploading credentials and certificates.
 */
const readFileAsBase64 = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = () => {
      const result = fileReader.result?.toString() || '';
      const base64Content = result.split('base64,')[1];

      if (!base64Content) {
        reject(new Error('Failed to extract base64 content from file'));
        return;
      }

      resolve(base64Content);
    };

    fileReader.onerror = () => {
      reject(
        new Error(
          `Failed to read file: ${fileReader.error?.message || 'Unknown error'}`
        )
      );
    };

    fileReader.onabort = () => {
      reject(new Error('File reading was aborted'));
    };

    fileReader.readAsDataURL(file);
  });
};

// ============================================================================
// Validation
// ============================================================================

/**
 * Validation rules for static form fields.
 * These fields are present across all source types.
 */
export const validationRules = {
  device_uid: {
    required: 'Device is required',
    validate: (_device_uid: string) => _device_uid.length > 0
  },
  source_type: {
    required: 'Source Type is required',
    validate: (_validSourceType: number) => _validSourceType !== 0
  },
  fetch_device_vuln: { required: 'Fetch Device Vulnerabilities is required' },
  vuln_scan_time_interval: {
    required: 'Vulnerability Scan Polling Interval is required'
  }
};

export const validateIPAddress = (ip: string) => {
  // Non Mandatory field so return true if empty
  if (!ip) return true;

  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  const ipv6Regex = /^([\da-f]{1,4}:){7}[\da-f]{1,4}$/i;
  if (ipv4Regex.test(ip)) {
    return ip.split('.').every((part) => parseInt(part, 10) <= 255);
  }
  if (ipv6Regex.test(ip)) {
    return ip.split(':').every((part) => part.length <= 4);
  }
  return false;
};

export const validateUrl = (value: string) => {
  if (!value) {
    return true;
  }

  const urlRegex =
    /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,6})(\/[\w.-]*)*(\?[\w&=.%+-]*)?(#[\w-]*)?$/i;

  return urlRegex.test(value) || 'Invalid URL format';
};

/** Validates whether a string matches the UUID format */
export const isValidUUID = (str: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return typeof str === 'string' && uuidRegex.test(str);
};

// ============================================================================
// Source Type Predicates
// ============================================================================

export const isEnterprisePlan = (selectedSubscriptionType: string): boolean => {
  return selectedSubscriptionType.includes('Enterprise plan');
};

export const isMicrosoftDefender = (sourceTypeId: number): boolean => {
  return sourceTypeId === SOURCE_TYPE.MICROSOFT_DEFENDER;
};

export const isMicrosoftEntraID = (sourceTypeId: number): boolean => {
  return sourceTypeId === SOURCE_TYPE.MICROSOFT_ENTRA_ID;
};

export const isMicrosoftChildSource = (typeId: number) => {
  return MICROSOFT_CHILD_SOURCE_TYPES.includes(typeId);
};

// ============================================================================
// Category Helpers
// ============================================================================

export const AAD_CATEGORY = 'AAD Identity Protection - Signin';

export const removeAADCategoryFromList = (list?: BasicSourceLogTypeDTO[]) => {
  return list?.filter((item) => item.name !== AAD_CATEGORY);
};

export const removeAADCategoryFromSelected = (selected?: string[]) => {
  return selected?.includes(AAD_CATEGORY)
    ? selected.filter((item) => item !== AAD_CATEGORY)
    : selected;
};

// ============================================================================
// UI Metadata
// ============================================================================

/** Fetch UI metadata from configuration */
export const getUiMetaData = (sourceTypeId: number, subSource?: string) => {
  return UI_META_DATA_CONFIG.find(
    (item) =>
      item.source_type_id === sourceTypeId &&
      item.sub_source === (subSource ?? '')
  );
};

// ============================================================================
// Payload Building - Helper Functions
// ============================================================================

/**
 * Builds the base source request with common fields.
 */
const buildBaseSourceRequest = (
  formData: SourceCreateDynamicFormModel
): SourceCreateRequest => {
  return {
    device_uid: formData.device_uid,
    source_type_id: Number(formData.source_type_id),
    ip_address: formData.ip_address || '',
    organization_id: formData?.org_id || 0,
    source_name: formData.source_name || '',
    site_name: formData.site_name || '',
    active: formData.active || false,
    connection_inputs: {} as ConnectionInputs,
    status_code: STATUS_CODES.NEW
  };
};

/**
 * Handles mode-specific payload modifications.
 * Business rules:
 * - Dropbox sources start as inactive in New mode (requires OAuth flow)
 * - Edit mode includes edit_status flag to trigger update logic
 * - AddThumbPrintId/AddAccessCode modes add specific completion fields
 */
const applyModeSpecificFields = (
  data: SourceCreateRequest,
  formData: SourceCreateDynamicFormModel,
  mode: string
): SourceCreateRequest => {
  // Dropbox: Must start inactive to complete OAuth flow
  if (
    mode === SourceDialogMode.New &&
    formData.source_type_id === SOURCE_TYPE.DROPBOX
  ) {
    data.active = false;
  }

  if (mode === SourceDialogMode.Edit) {
    data.edit_status = formData.edit_status || false;

    // Track newly added categories/providers for differential updates
    if (formData?.newly_added_category) {
      data.newly_added_category = formData.newly_added_category;
    }
    if (formData?.newly_added_cloud_provider) {
      data.newly_added_cloud_provider = formData.newly_added_cloud_provider;
    }
    if (formData?.removed_categories) {
      data.removed_categories = formData.removed_categories;
    }
  }

  // Microsoft 365: Complete thumbprint setup
  if (mode === SourceDialogMode.AddThumbPrintId && formData.thumbprint_id) {
    data.thumbprint_id = formData.thumbprint_id || '';
    data.source_uid = formData.source_uid || '';
    data.source_name = formData.source_name || '';
  }

  // Dropbox: Complete OAuth access code
  if (mode === SourceDialogMode.AddAccessCode && formData.access_code) {
    data.access_code = formData.access_code || '';
    data.source_uid = formData.source_uid || '';
    data.source_name = formData.source_name || '';
    data.active = true;
  }

  return data;
};

/**
 * Determines which form fields should be excluded from connection_inputs.
 * Business rules:
 * - CrowdStrike: cloud_provider only needed if CSPMIOAStreamingEvent category selected
 * - Juniper Mist: sub_time_interval only needed if Tickets/GuestAuthorization selected
 * - Categories excluded if show_categories is false (legacy sources)
 * - Empty contact_email excluded to keep payload clean
 */
const buildAttributeExclusionList = (
  formData: SourceCreateDynamicFormModel,
  connectionInputSchema: ConnectionInputSchema,
  sourceTypeId: number
): string[] => {
  const exclusions: string[] = [...ROOT_LEVEL_ATTRIBUTES];

  // CrowdStrike: cloud_provider only needed for CSPM IOA events
  if (sourceTypeId === SOURCE_TYPE.CROWD_STRIKE) {
    if (!formData?.category?.includes('CSPMIOAStreamingEvent')) {
      exclusions.push('cloud_provider');
    }
  }

  // Juniper Mist: sub_time_interval only for specific categories
  const hasJuniperMistCategory =
    formData?.category?.includes('Tickets') ||
    formData?.category?.includes('GuestAuthorization');
  if (sourceTypeId === SOURCE_TYPE.JUNIPER_MIST) {
    if (!hasJuniperMistCategory) {
      exclusions.push('sub_time_interval');
    }
  }

  // Legacy sources without category support
  if (!connectionInputSchema?.show_categories) {
    exclusions.push('category');
  }

  // Clean up empty optional fields
  if (!formData?.contact_email) {
    exclusions.push('contact_email');
  }

  return exclusions;
};

/**
 * Populates connection_inputs with all dynamic form fields.
 * Excludes root-level attributes and conditionally excluded fields.
 */
const buildConnectionInputs = (
  formData: SourceCreateDynamicFormModel,
  exclusionList: string[]
): ConnectionInputs => {
  const connectionInputs: Record<string, unknown> = {};

  const normalizeTopics = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (
          item &&
          typeof item === 'object' &&
          'value' in item &&
          typeof item.value === 'string'
        ) {
          return item.value;
        }

        return '';
      })
      .filter(Boolean);
  };

  Object.keys(formData).forEach((key) => {
    if (!exclusionList.includes(key)) {
      if (key === 'topics') {
        connectionInputs[key] = normalizeTopics(
          formData[key as keyof typeof formData]
        );
        return;
      }

      connectionInputs[key] = formData[key as keyof typeof formData];
    }
  });

  return connectionInputs as ConnectionInputs;
};

/**
 * Adds sub_source_type specific fields to connection_inputs.
 * Business rules:
 * - Mimecast: API version affects endpoint structure
 * - Cisco Secure Endpoint: Queue setup determines event streaming mode
 * - Microsoft Entra ID: API endpoint type determines data scope
 * - Qualys: Authentication method affects credential requirements
 */
const applySubSourceTypeLogic = (
  data: SourceCreateRequest,
  formData: SourceCreateDynamicFormModel,
  mode: string,
  sourceTypeId: number
): SourceCreateRequest => {
  const subSourceType = formData?.sub_source_type;

  if (!subSourceType) {
    return data;
  }

  // Mimecast: API version selection
  if (sourceTypeId === SOURCE_TYPE.MIMECAST) {
    const is_api_v2 = subSourceType === 'Version2';
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      is_api_v2
    };
  }

  // Cisco Secure Endpoint: Queue setup for streaming
  if (sourceTypeId === SOURCE_TYPE.CISCO_SECURE_ENDPOINT) {
    const queue_setup_enabled = subSourceType === 'Yes';
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      queue_setup_enabled
    };

    // Track queue name changes in edit mode for backend processing
    if (mode === SourceDialogMode.Edit) {
      data.connection_inputs = {
        ...(data.connection_inputs as Record<string, unknown>),
        name_changed: formData.is_queue_name_changed
      };
    }
  }

  // Microsoft Entra ID: API endpoint type
  if (sourceTypeId === SOURCE_TYPE.MICROSOFT_ENTRA_ID) {
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      api_endpoint_type: subSourceType
    };
  }

  // Qualys: Authentication method
  if (sourceTypeId === SOURCE_TYPE.QUALYS) {
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      authentication_method: subSourceType || ''
    };
  }

  return data;
};

/**
 * Handles date field conversions from local to UTC.
 * All dates are stored in UTC for consistency across user timezones.
 */
const applyDateConversions = (
  data: SourceCreateRequest,
  formData: SourceCreateDynamicFormModel
): SourceCreateRequest => {
  // Token expiry date
  if (formData?.expires) {
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      expires: convertDateToUTC(formData.expires)
    };
  }

  // Historical data ingestion start date
  if (formData?.ingest_from) {
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      ingest_from: convertDateToUTC(formData.ingest_from)
    };
  }

  // Certificate expiry date
  if (formData?.certificate_expires) {
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      expires: convertDateToUTC(formData.certificate_expires)
    };
  }

  // Backup credentials expiry
  if (formData?.backup_expires) {
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      backup_expires: convertDateToUTC(formData.backup_expires)
    };
  }

  return data;
};

/**
 * Removes expiry fields for source types where they are optional.
 * Business rules:
 * - Proofpoint POD: Uses rotating tokens, no fixed expiry
 * - Soft expiry sources: Token refresh handled automatically
 */
const cleanupExpiryFields = (
  data: SourceCreateRequest,
  sourceTypeId: number
): SourceCreateRequest => {
  let connectionInputs = data.connection_inputs ?? {};

  if (sourceTypeId === SOURCE_TYPE.PROOFPOINT_POD) {
    // Remove both primary and backup expiry for Proofpoint POD
    const {
      expires: _expires,
      backup_expires: _backup_expires,
      ...rest
    } = connectionInputs;
    connectionInputs = rest;
  } else if (SOFT_EXPIRY_SOURCE_TYPES.has(sourceTypeId ?? -1)) {
    // Remove primary expiry only for soft expiry sources
    const { expires: _expires, ...rest } = connectionInputs;
    connectionInputs = rest;
  }

  return {
    ...data,
    connection_inputs: connectionInputs
  };
};

/**
 * Handles file upload for sources that require credential files.
 * Business rules:
 * - File converted to Base64 for JSON transport
 * - In Edit mode, tracks whether file was updated
 */
const applyFileUploads = async (
  data: SourceCreateRequest,
  formData: SourceCreateDynamicFormModel,
  mode: string,
  sourceTypeId: number
): Promise<SourceCreateRequest> => {
  // Only process file uploads for supported source types
  if (!FILE_UPLOAD_SOURCE_TYPES.includes(sourceTypeId as number)) {
    return data;
  }

  if (typeof formData?.file_upload === 'object') {
    const file = formData.file_upload as File;
    data.file_name = file?.name || '';
    data.file_content = await readFileAsBase64(file);

    if (mode === SourceDialogMode.Edit) {
      data.is_file_update = true;
    }

    // Remove from connection_inputs to avoid duplication
    delete data.connection_inputs.file_upload;
  } else if (mode === SourceDialogMode.Edit) {
    data.is_file_update = false;
  }

  return data;
};

/**
 * Handles certificate authentication for Microsoft sources.
 * Business rules:
 * - Two flows: new certificate (generated by backend) or existing (uploaded by user)
 * - New certificate flow: Backend generates keypair from provided metadata
 * - Existing certificate flow: User uploads public/private key files
 * - Certificate regeneration in Edit mode triggers special backend processing
 */
const applyCertificateAuthentication = async (
  data: SourceCreateRequest,
  formData: SourceCreateDynamicFormModel,
  mode: string,
  authenticationMode: string,
  newCertificate: boolean,
  regenerateCertificate: boolean,
  connectionInputSchema: ConnectionInputSchema,
  sourceTypeId: number
): Promise<SourceCreateRequest> => {
  // Only apply to Microsoft sources
  if (!CERTIFICATE_SUPPORTED_SOURCE_TYPES.includes(sourceTypeId as number)) {
    return data;
  }

  if (authenticationMode === 'client_secret') {
    // Client secret flow: simple bearer token authentication
    data.connection_inputs = {
      ...data.connection_inputs,
      is_certificate_flow: false,
      is_new_certificate_flow: false
    };
  } else if (authenticationMode === 'certificate') {
    // Certificate flow: more secure, uses certificate-based auth

    // Remove client_secret if present to avoid confusion
    const { client_secret: _client_secret, ...connectionInputs } =
      data.connection_inputs ?? {};

    data.connection_inputs = {
      ...connectionInputs,
      is_certificate_flow: true,
      is_new_certificate_flow: newCertificate
    };

    // Existing certificate: User uploads public/private key files
    if (
      !newCertificate &&
      typeof formData?.public_key !== 'undefined' &&
      typeof formData?.private_key !== 'undefined'
    ) {
      const publicKey = formData.public_key as unknown as File;
      const privateKey = formData.private_key as unknown as File;

      const [publicKeyBase64, privateKeyBase64] = await Promise.all([
        readFileAsBase64(publicKey),
        readFileAsBase64(privateKey)
      ]);

      // Collect certificate metadata from form
      connectionInputSchema?.existing_certificate_inputs?.forEach((field) => {
        data.cert_data = {
          ...data.cert_data,
          [field.name]: formData[field.name as keyof typeof formData]
        };
      });

      data.cert_data = {
        ...data.cert_data,
        public_key: publicKeyBase64,
        private_key: privateKeyBase64,
        private_key_file_name: privateKey?.name || '',
        public_key_file_name: publicKey?.name || ''
      };

      if (
        mode === SourceDialogMode.Edit &&
        regenerateCertificate &&
        data.cert_data
      ) {
        data.edit_status = true;
        data.cert_regenerate = true;
      }
    }
    // New certificate: Backend generates keypair from metadata
    else if (newCertificate) {
      connectionInputSchema?.new_certificate_inputs?.forEach((field) => {
        data.cert_data = {
          ...data.cert_data,
          [field.name]: formData[field.name as keyof typeof formData]
        };
      });

      if (
        mode === SourceDialogMode.Edit &&
        regenerateCertificate &&
        data.cert_data
      ) {
        data.edit_status = true;
        data.cert_regenerate = true;
      }
    }
  }

  // Handle existing thumbprint selection
  if (
    formData?.existing_thumbprint_id &&
    authenticationMode === 'certificate'
  ) {
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      thumbprint_id: formData.existing_thumbprint_id
    };
  }

  return data;
};

/**
 * Handles data provider payloads for container-based ingestion.
 * Business rules:
 * - AWS S3: Multiple S3 buckets with different configurations
 * - Google Storage: Multiple GCS buckets
 * - Microsoft: Complex multi-provider setup with separate sources
 */
const applyDataProviderPayloads = async (
  data: SourceCreateRequest,
  formData: SourceCreateDynamicFormModel,
  mode: SourceDialogMode,
  authenticationMode: string,
  newCertificate: boolean,
  orgLabel: string = ''
): Promise<SourceCreateRequest> => {
  const sourceTypeId = Number(formData.source_type_id);

  // AWS S3: Container-based log ingestion
  if (formData?.data_provider && sourceTypeId === SOURCE_TYPE.AWS_S3) {
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      s3_data_providers: buildS3DataProviderPayload(formData)
    };
  }

  // Google Storage: Container-based log ingestion
  if (formData?.data_provider && sourceTypeId === SOURCE_TYPE.GOOGLE_STORAGE) {
    data.connection_inputs = {
      ...(data.connection_inputs as Record<string, unknown>),
      data_providers: buildGoogleStoragePayload(formData)
    };
  }

  // Microsoft: Complex multi-provider payload (O365, Defender, Sentinel, etc.)
  if (formData?.data_provider && sourceTypeId === SOURCE_TYPE.MICROSOFT) {
    const microsoftPayload = buildMicrosoftPayload(
      formData,
      mode,
      authenticationMode,
      newCertificate,
      orgLabel
    );

    // Microsoft payload replaces entire data structure
    data = {
      ...data,
      ...microsoftPayload,
      source_type_id: Number(
        microsoftPayload.source_type_id ?? formData.source_type_id
      ),
      source_name: microsoftPayload.source_name ?? formData.source_name ?? '',
      site_name: microsoftPayload.site_name ?? formData.site_name ?? '',
      ip_address: microsoftPayload.ip_address ?? formData.ip_address ?? '',
      device_uid: microsoftPayload.device_uid ?? formData.device_uid ?? '',
      organization_id:
        microsoftPayload.organization_id ?? formData.organization_id ?? 0
    };
  }

  return data;
};

// ============================================================================
// Main Payload Mapping Function
// ============================================================================

/**
 * Transforms form data into the API payload format.
 *
 * This function orchestrates all payload transformations:
 * 1. Build base request structure
 * 2. Apply mode-specific fields
 * 3. Build connection_inputs from dynamic fields
 * 4. Apply source-type-specific logic
 * 5. Handle file uploads and certificates
 * 6. Clean up optional fields
 *
 * @param formData - Complete form values
 * @param mode - Dialog mode (New, Edit, AddThumbPrintId, AddAccessCode)
 * @param authenticationMode - Auth method for Microsoft sources
 * @param newCertificate - Whether generating new certificate
 * @param regenerateCertificate - Whether regenerating in edit mode
 * @param connectionInputSchema - Dynamic schema for current source type
 * @returns API-ready payload
 */
export async function mapFormDataToSourceCreateBase(
  formData: SourceCreateDynamicFormModel,
  mode: string,
  authenticationMode: string,
  newCertificate: boolean,
  regenerateCertificate: boolean,
  connectionInputSchema: ConnectionInputSchema,
  orgLabel: string = ''
) {
  // Performance Optimization: Parse source type ID once and pass to all helpers
  // This avoids repeated Number() conversions throughout the payload building process
  const sourceTypeId = Number(formData?.source_type_id);

  // Step 1: Build base request structure
  let data = buildBaseSourceRequest(formData);

  // Step 2: Apply mode-specific modifications
  data = applyModeSpecificFields(data, formData, mode);

  // Step 3: Determine field exclusions and build connection_inputs
  const exclusionList = buildAttributeExclusionList(
    formData,
    connectionInputSchema,
    sourceTypeId
  );
  data.connection_inputs = buildConnectionInputs(formData, exclusionList);

  // Step 4: Apply data provider payloads (must be before other connection_input modifications)
  data = await applyDataProviderPayloads(
    data,
    formData,
    mode as SourceDialogMode,
    authenticationMode,
    newCertificate,
    orgLabel
  );

  // Step 5: Apply sub-source type logic
  data = applySubSourceTypeLogic(data, formData, mode, sourceTypeId);

  // Step 6: Convert dates to UTC
  data = applyDateConversions(data, formData);

  // Step 7: Clean up expiry fields for sources that don't use them
  data = cleanupExpiryFields(data, sourceTypeId);

  // Step 8: Handle file uploads
  data = await applyFileUploads(data, formData, mode, sourceTypeId);

  // Step 9: Handle certificate authentication
  data = await applyCertificateAuthentication(
    data,
    formData,
    mode,
    authenticationMode,
    newCertificate,
    regenerateCertificate,
    connectionInputSchema,
    sourceTypeId
  );

  return data;
}

/**
 * Determines the category filter suffix for Microsoft Entra ID sub-providers.
 *
 * BUSINESS CONTEXT:
 * Microsoft Entra ID has two API types that support different categories:
 * - Graph API: Supports categories ending in "Graph" (newer, recommended)
 * - O365 API: Supports categories ending in "O365" (legacy, maintenance mode)
 *
 * The suffix is used to filter available categories shown to the user based on
 * which API they're configuring.
 *
 * @param subProvider - The Microsoft sub-provider being configured
 * @returns Category name suffix to filter by, or undefined for non-Entra providers
 */
export const getCategoryFilterSuffix = (
  subProvider?: string
): string | undefined => {
  if (subProvider === 'GraphAPI') {
    return 'Graph';
  }
  if (subProvider === 'O365API') {
    return 'O365';
  }
  return undefined;
};
